import { useMemo, useState } from "react";
import { audit, type AuditEvent } from "../../api/client";
import { useDuviCoreAdminQuery } from "../../hooks/use-api";

export const DUVI_CORE_ADMIN_AUDIT_ACTION_OPTIONS = [
  { value: "created", label: "Created" },
  { value: "updated", label: "Updated" },
  { value: "deleted", label: "Deleted" },
  { value: "invited", label: "Invited" },
  { value: "disabled", label: "Disabled" },
  { value: "resolved", label: "Resolved" },
  { value: "pushed", label: "Pushed" },
];

export const DUVI_CORE_ADMIN_AUDIT_ENTITY_OPTIONS = [
  { value: "ticket", label: "Ticket" },
  { value: "article", label: "Article" },
  { value: "user", label: "User" },
];

export interface DuviCoreAdminAuditRow {
  id: string;
  timestamp: string;
  actor: { type: "system" | "user"; name: string };
  action: string;
  entity: { type: string; name: string };
  detail: string;
  ip: string;
  _raw: AuditEvent;
}

export function useDuviCoreAdminAuditLog() {
  const [selectedActor, setSelectedActor] = useState("all");
  const [selectedActions, setSelectedActions] = useState<string[]>([]);
  const [selectedEntities, setSelectedEntities] = useState<string[]>([]);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [selectedEvent, setSelectedEvent] = useState<AuditEvent | null>(null);
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [cursorStack, setCursorStack] = useState<Array<string | undefined>>([]);

  const { data, meta, isLoading } = useDuviCoreAdminQuery(
    () =>
      audit.list({
        cursor,
        actor: selectedActor !== "all" ? selectedActor : undefined,
        action: selectedActions.join(",") || undefined,
        entity_type: selectedEntities.join(",") || undefined,
        date_from: dateFrom || undefined,
        date_to: dateTo || undefined,
      }),
    [
      cursor,
      selectedActor,
      selectedActions,
      selectedEntities,
      dateFrom,
      dateTo,
    ],
  );

  const rawEvents: AuditEvent[] = data ?? [];

  const events: DuviCoreAdminAuditRow[] = rawEvents.map((e) => {
    const actorName = e.actor_name || e.actor_id || "System";
    const isSystem = !e.actor_id && !e.actor_name;
    return {
      id: e.event_id,
      timestamp: e.timestamp,
      actor: { type: isSystem ? "system" : "user", name: actorName },
      action: e.action ?? "",
      entity: {
        type: e.entity_type ?? "",
        name: e.entity_name ?? e.entity_id ?? "",
      },
      detail: e.detail ?? "",
      ip: e.ip_address ?? "",
      _raw: e,
    };
  });

  const actorOptions = useMemo(() => {
    const seen = new Map<string, string>();
    for (const e of rawEvents) {
      const id = e.actor_id || "system";
      if (!seen.has(id)) seen.set(id, e.actor_name || e.actor_id || "System");
    }
    return [
      { value: "all", label: "All users" },
      ...Array.from(seen, ([value, label]) => ({ value, label })),
    ];
  }, [rawEvents]);

  const nextCursor = meta?.cursor ?? null;
  const hasNext = Boolean(nextCursor);
  const hasPrev = cursorStack.length > 0;

  const resetPagination = () => {
    setCursor(undefined);
    setCursorStack([]);
  };

  const handleNext = () => {
    if (!hasNext || !nextCursor) return;
    setCursorStack((s) => [...s, cursor]);
    setCursor(nextCursor);
  };

  const handlePrev = () => {
    if (!hasPrev) return;
    setCursorStack((s) => {
      const next = s.slice(0, -1);
      setCursor(s[s.length - 1]);
      return next;
    });
  };

  const handleExportCsv = () => {
    if (!events.length) return;
    const headers = [
      "Timestamp",
      "Actor",
      "Action",
      "Entity Type",
      "Entity Name",
      "Detail",
      "IP Address",
    ];
    const rows = events.map((e) => [
      e.timestamp,
      e.actor.name,
      e.action,
      e.entity.type,
      e.entity.name,
      e.detail,
      e.ip,
    ]);
    const escape = (v: string) => `"${String(v).replace(/"/g, '""')}"`;
    const csv = [headers, ...rows]
      .map((r) => r.map(escape).join(","))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `audit-log-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  // Filter setters that also reset pagination, matching the prior behaviour.
  const onActorChange = (v: string) => {
    setSelectedActor(v);
    resetPagination();
  };
  const onActionsChange = (v: string[]) => {
    setSelectedActions(v);
    resetPagination();
  };
  const onEntitiesChange = (v: string[]) => {
    setSelectedEntities(v);
    resetPagination();
  };
  const onDateFromChange = (v: string) => {
    setDateFrom(v);
    resetPagination();
  };
  const onDateToChange = (v: string) => {
    setDateTo(v);
    resetPagination();
  };

  return {
    events,
    isLoading,
    actorOptions,
    selectedActor,
    onActorChange,
    selectedActions,
    onActionsChange,
    selectedEntities,
    onEntitiesChange,
    dateFrom,
    onDateFromChange,
    dateTo,
    onDateToChange,
    selectedEvent,
    setSelectedEvent,
    hasNext,
    hasPrev,
    handleNext,
    handlePrev,
    handleExportCsv,
  };
}
